import { getUserGroups } from "@/helpers/apis";
import { useAppDispatch, useAppState } from "@/modules/app/useAppContext";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useInfiniteQuery } from '@tanstack/react-query';
import { UserGroup } from "./types";
import { setUserGroupsLoaded } from "@/modules/app/appSlice";

const useUserGroups = () => {
  const [search, setSearch] = useState("");
  const { rbacEnabled } = useAppState();
  const dispatch = useAppDispatch();

  const query = useInfiniteQuery({
    queryKey: ["UserGroups", search],
    initialPageParam: 1,
    queryFn: ({ pageParam }) => getUserGroups({ page: pageParam, search }),
    enabled: rbacEnabled,
    gcTime: 1000 * 60 * 2, // 2 mins
    getNextPageParam: (lastPage, _allPages) => {
      if (!lastPage) return undefined;
      const totalPages = lastPage.pages;
      const currentPage = lastPage.page;
      return currentPage >= totalPages ? undefined : currentPage + 1;
    },
  });

  useEffect(() => {
    // Only set userGroupsLoaded to true when all pages have been loaded
    // This ensures that RBAC filtering works correctly for subsequent queries
    if (!query.isLoading && !query.hasNextPage) {
      dispatch(setUserGroupsLoaded(true));
    } else if (query.isLoading || query.hasNextPage) {
      // Reset to false if we're loading or have more pages to load
      dispatch(setUserGroupsLoaded(false));
    }
  }, [dispatch, query.isLoading, query.hasNextPage]);

  const groups = useMemo(() => {
    const result: UserGroup[] = [];
    query.data?.pages.forEach(page => {
      if (page?.items) {
        result.push(...page.items);
      }
    });
    return result;
  }, [query.data]);

  const getTeamNames = useCallback(
    (teamIds: string[] | null): string[] => {
      if (!teamIds || teamIds.length === 0) return ["All org"];

      const teamNames = teamIds
        .map(teamId => {
          const team = groups?.find(g => g.id.toString() === teamId.toString());
          return team?.name;
        })
        .filter((name): name is string => Boolean(name));

      return teamNames.length > 0 ? teamNames : ["All org"];
    },
    [groups]
  );

  return {
    ...query,
    search,
    setSearch,
    groups,
    total: query.data?.pages[0]?.total,
    getTeamNames
  };
};

export default useUserGroups;
